import React, { useState, useEffect, useRef } from 'react';
import { parseSRT, getCurrentSubtitle } from '../../../utils/srtParser';
import Modal from 'react-bootstrap/Modal';
import './style.scss';

const AudioImageModal = ({ show, onClose, audioSrc, srtSrc, imageSrc }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [subtitles, setSubtitles] = useState([]);
  const [currentSubtitle, setCurrentSubtitle] = useState(null);
  const [showSubtitles] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  // Stop audio when modal closes
  useEffect(() => {
    if (!show && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  }, [show]);

  // Load SRT file
  useEffect(() => {
    if (srtSrc && show) {
      fetch(srtSrc)
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.text();
        })
        .then(text => {
          const parsed = parseSRT(text);
          setSubtitles(parsed);
          setIsLoading(false);
        })
        .catch(error => {
          console.error('Error loading SRT file:', error);
          setIsLoading(false);
        });
    }
  }, [srtSrc, show]);

  // Auto-play when modal opens
  useEffect(() => {
    const audio = audioRef.current;
    if (show && audio && !isLoading) {
      const tryPlay = () => {
        if (audio && audio.readyState >= 2) {
          const playPromise = audio.play();
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                setIsPlaying(true);
              })
              .catch(error => {
                console.error('Auto-play failed:', error);
              });
          }
        }
      };

      const handleCanPlay = () => {
        tryPlay();
      };

      audio.addEventListener('canplay', handleCanPlay);
      const timeout = setTimeout(() => tryPlay(), 500);

      return () => {
        if (audio) {
          audio.removeEventListener('canplay', handleCanPlay);
        }
        clearTimeout(timeout);
      };
    }
  }, [show, isLoading]);

  // Update current subtitle based on playback time
  useEffect(() => {
    if (isPlaying) {
      const interval = setInterval(() => {
        if (audioRef.current) {
          const time = audioRef.current.currentTime;
          setCurrentTime(time);
          if (subtitles.length > 0) {
            const subtitle = getCurrentSubtitle(subtitles, time);
            setCurrentSubtitle(subtitle);
          }
        }
      }, 100);

      return () => clearInterval(interval);
    }
  }, [subtitles, isPlaying]);

  // Handle audio events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    };

    const handleLoadedData = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    };

    const handleDurationChange = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    };

    const handleTimeUpdate = () => {
      if (audio.currentTime !== undefined && isFinite(audio.currentTime)) {
        setCurrentTime(audio.currentTime);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    const handleCanPlay = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('loadeddata', handleLoadedData);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    // Try to get duration immediately if already loaded
    if (audio.readyState >= 2 && audio.duration && isFinite(audio.duration)) {
      setDuration(audio.duration);
    }

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('loadeddata', handleLoadedData);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [show]);

  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleProgressChange = (e) => {
    if (audioRef.current && duration > 0) {
      const newTime = (parseFloat(e.target.value) / 100) * duration;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleProgressInput = (e) => {
    // Update in real-time while dragging
    if (audioRef.current && duration > 0) {
      const newTime = (parseFloat(e.target.value) / 100) * duration;
      setCurrentTime(newTime);
    }
  };

  const formatTime = (time) => {
    if (!isFinite(time)) return '0:00';
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!show) return null;

  return (
    <Modal
      show={show}
      onHide={onClose}
      dialogClassName="audio-image-modal"
      backdrop={true}
      keyboard={true}
      centered
    >
      <Modal.Body className="audio-image-modal-body">
        <div className="audio-image-close-button" onClick={onClose}>
          ×
        </div>

        {/* Animated Image Container */}
        <div className="audio-image-container">
          <div className="image-wrapper">
            <img
              src={imageSrc}
              alt="Hariti & Panchika"
              className="animated-image"
            />
          </div>
        </div>

        {/* Audio Player */}
        <div className="audio-player-section">
          <audio ref={audioRef} src={audioSrc} preload="auto" />

          {/* Controls */}
          <div className="audio-controls">
            <button className="play-pause-btn" onClick={togglePlayPause}>
              {isPlaying ? '⏸' : '▶'}
            </button>

            <div className="progress-container">
              <input
                type="range"
                min="0"
                max="100"
                step="0.1"
                value={duration > 0 ? (currentTime / duration) * 100 : 0}
                onChange={handleProgressChange}
                onInput={handleProgressInput}
                className="progress-bar"
                style={{
                  '--seek-before-width': duration > 0 ? `${(currentTime / duration) * 100}%` : '0%'
                }}
              />
            </div>

            <div className="time-display">
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>
          </div>

          {/* Subtitle Display */}
          {showSubtitles && currentSubtitle && (
            <div className="subtitle-display">
              {currentSubtitle.text}
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default AudioImageModal;

